/** Keyboard and gamepad navigation for player-facing menu surfaces. Native form controls keep
 * their own arrow-key behaviour; everything else follows the visible focus order. */
const selector = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function visibleControls(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(selector)].filter((el) => {
    const style = getComputedStyle(el);
    return !el.closest('[hidden]') && style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
  });
}

export function mountMenuNavigation(root: HTMLElement, onBack?: () => void): () => void {
  let frame = 0;
  let lastMove = 0;
  let confirmDown = false;
  let backDown = false;

  const move = (delta: number) => {
    const controls = visibleControls(root);
    if (!controls.length) return;
    const current = document.activeElement as HTMLElement | null;
    const index = Math.max(0, controls.indexOf(current ?? controls[0]!));
    controls[(index + delta + controls.length) % controls.length]!.focus();
  };
  const onKey = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (event.key === 'Escape' && onBack) { event.preventDefault(); onBack(); return; }
    if (target.matches('input, select, textarea, [contenteditable="true"]')) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
  };
  const poll = (time: number) => {
    const pad = navigator.getGamepads?.().find(Boolean);
    if (pad) {
      const forward = !!(pad.buttons[13]?.pressed || pad.buttons[15]?.pressed || (pad.axes[1] ?? 0) > 0.55 || (pad.axes[0] ?? 0) > 0.55);
      const backward = !!(pad.buttons[12]?.pressed || pad.buttons[14]?.pressed || (pad.axes[1] ?? 0) < -0.55 || (pad.axes[0] ?? 0) < -0.55);
      if ((forward || backward) && time - lastMove > 180) { move(forward ? 1 : -1); lastMove = time; }
      const confirm = !!pad.buttons[0]?.pressed;
      if (confirm && !confirmDown) (document.activeElement as HTMLElement | null)?.click();
      confirmDown = confirm;
      const back = !!pad.buttons[1]?.pressed;
      if (back && !backDown) onBack?.();
      backDown = back;
    }
    frame = requestAnimationFrame(poll);
  };
  root.addEventListener('keydown', onKey);
  frame = requestAnimationFrame(poll);
  return () => { root.removeEventListener('keydown', onKey); cancelAnimationFrame(frame); };
}
